import type { ComponentType, ReactNode } from 'react';
import { CommandItem } from '../../components/ui/command';

export interface PaletteSearchResultProps {
  /** Stable identifier used for cmdk selection/deduplication. */
  value: string;
  /** Primary label. Used by cmdk's internal fuzzy matcher. */
  label: string;
  /** Optional secondary text shown to the right of the label. */
  hint?: string;
  /** Optional icon rendered on the left. */
  icon?: ComponentType<{ className?: string }>;
  /** Optional trailing content (e.g. a status badge). */
  trailing?: ReactNode;
  /** Invoked when the user selects the result. */
  onSelect: () => void;
  /**
   * Extra keywords for the fuzzy matcher (e.g. tags, alternate names).
   * Matched independently of the visible label.
   */
  keywords?: string[];
}

/**
 * Helper component that search providers render for each result. Wraps a cmdk
 * `CommandItem` with the standard label/icon/hint layout used across the app.
 * Providers should rely on this instead of hand-rolling `CommandItem` markup so
 * the look stays consistent between the global palette and inline pickers.
 *
 * `forceMount` stays on so providers that already rank/filter their own rows
 * are not re-filtered by cmdk when the global palette leaves filtering enabled.
 */
export function PaletteSearchResult({
  value,
  label,
  hint,
  icon: Icon,
  trailing,
  onSelect,
  keywords,
}: PaletteSearchResultProps) {
  return (
    <CommandItem value={value} keywords={keywords} forceMount onSelect={onSelect}>
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span className="truncate">{label}</span>
      {hint ? <span className="ml-2 text-xs text-muted-foreground">{hint}</span> : null}
      {trailing ? <span className="ml-auto">{trailing}</span> : null}
    </CommandItem>
  );
}
